// Runs the Collab page: lets a person make a Collab link, join a place with it under a name, and
// use the place, a stage of everyone there with a dock of controls and one side panel for the
// people and files, until they leave or the connection closes.
//
// Collab: the home that makes a Collab link, the lobby that joins with a name, and the
// place: the connection, people, the board, talk and files.
// Built on the SDK (`tablebox.io`), as any developer's app would be.
// The place is the token's room: each person gives their name as the state `name`, and everyone
// there who gives one is a person.

import { connect, State, type Connection } from 'tablebox.io';
import { boardSection } from './board.ts';
import { avatar, copyButton, h, icon, iconButton, lang, languageSelect, panelHead, show, t, tx, type Child, type IconName } from './dom.ts';
import { filesSection } from './files.ts';
import { formatCollabLink, parseCollabLink } from './link.ts';
import { talkSection } from './media.ts';
import { hueOf, initials, reasonText } from './text.ts';

const app = document.getElementById('app')!;
const base = location.href.split('#')[0];

// Kept in the browser: the name and, per tab, whether this tab is connected, so that a reload
// connects again; the SDK keeps the tab's secret, so it connects as the same ID.
const NAME = 'collab-name';
const INSIDE = 'collab-inside';
const stored = (storage: () => Storage, key: string, value?: string | null): string | null => {
  try {
    if (value === null) storage().removeItem(key);
    else if (value !== undefined) storage().setItem(key, value);
    return storage().getItem(key);
  } catch {
    return value ?? null;
  }
};
const local = () => localStorage;
const session = () => sessionStorage;

let unloading = false;
let current: Connection | undefined;
addEventListener('hashchange', () => location.reload());
// Leaving the page closes the connection, so the others see the leave at once; a page brought back
// from the back-forward cache is loaded again, and joins as it was.
addEventListener('pagehide', () => {
  unloading = true;
  current?.close();
});
addEventListener('pageshow', event => event.persisted && location.reload());

/** Every screen's top bar: the brand, and what the place shows beside it. */
function topbar(...middle: Child[]): HTMLElement {
  return h('header', { class: 'topbar' },
    h('a', { class: 'brand', href: base }, h('span', { class: 'name' }, () => t('appName')), h('span', { class: 'product' }, 'Tablebox')),
    middle);
}

/** The home and the lobby: one card in the middle of the page, with the language choice under it (in the place, it is in the settings menu). */
function shell(...children: Child[]): void {
  app.className = '';
  app.onkeydown = null;
  show(app, topbar(), h('main', { class: 'center' },
    h('section', { class: 'card' }, children),
    h('label', { class: 'language-pick' }, icon('globe'), languageSelect())));
}

function field(id: string, label: () => string, control: HTMLInputElement): HTMLElement {
  control.id = id;
  return h('div', { class: 'field' }, h('label', { for: id }, label), control);
}

/** Collab's address alone: the home, which makes a Collab link. */
function formScreen(): void {
  const token = h('input', { required: true, autocomplete: 'off', spellcheck: 'false' });
  const problem = h('p', { class: 'problem', role: 'alert', hidden: true });
  const result = h('div', { class: 'result', 'aria-live': 'polite' });
  const make = (event: SubmitEvent) => {
    event.preventDefault();
    problem.hidden = token.value.trim() !== '';
    if (!problem.hidden) return show(problem, () => t('formIncomplete'));
    const url = formatCollabLink(base, token.value.trim());
    show(result,
      h('label', { for: 'collab-link' }, () => t('collabLink')),
      h('input', { id: 'collab-link', readonly: true, value: url, onfocus: (event: FocusEvent) => (event.target as HTMLInputElement).select() }),
      h('div', { class: 'row' }, copyButton(() => url), h('a', { href: url, id: 'open-link', class: 'button primary' }, () => t('openLink'))),
      h('a', { href: base, id: 'new-link', onclick: (event: MouseEvent) => {
        event.preventDefault();
        show(result);
        token.value = '';
        token.focus();
      } }, () => t('newLink')));
  };
  shell(h('h1', {}, () => t('formTitle')),
    h('form', { onsubmit: make },
      field('token', () => t('tokenLabel'), token),
      problem,
      h('button', { type: 'submit', id: 'make-link', class: 'gradient' }, () => t('makeLink'))),
    result);
}

/** The lobby: the name, then connecting; after a close or leaving, why, and joining again. */
function enterScreen(link: string, outcome?: { reason?: string }): void {
  const name = h('input', { id: 'name', required: true, maxlength: '64', autocomplete: 'nickname', value: stored(local, NAME) ?? '',
    placeholder: () => t('nameLabel'), 'aria-label': () => t('nameLabel'), oninput: () => face() });
  const disc = avatar(name.value, initials(name.value), 'large');
  const face = () => {
    disc.textContent = initials(name.value);
    disc.style.setProperty('--hue', String(hueOf(name.value)));
  };
  const button = h('button', { type: 'submit', id: 'enter', class: 'gradient' }, () => t(outcome ? 'enterAgain' : 'enter'));
  const submit = (event: SubmitEvent) => {
    event.preventDefault();
    const value = name.value.trim();
    if (!value) return;
    stored(local, NAME, value);
    button.disabled = true;
    show(button, () => t('connecting'));
    enter(link, value);
  };
  const reason = outcome?.reason;
  const message = outcome && h('p', { class: reason ? 'notice bad' : 'notice', role: 'alert', id: 'outcome', 'data-reason': reason ?? 'left' },
    reason ? () => t('closedOutcome', { reason: reasonText(lang, reason) }) : () => t('leftOutcome'));
  shell(message, disc,
    h('h1', {}, () => t('enterTitle')),
    h('form', { onsubmit: submit }, name, button),
    h('a', { href: base, id: 'new-link' }, () => t('newLink')));
}

function enter(link: string, name: string): void {
  // A token that is not a Tablebox token for one room is refused at once.
  void connect(link).then(connection => {
    stored(session, INSIDE, location.hash);
    placeScreen(link, connection, name);
  }, () => enterScreen(link, { reason: 'invalid-token' }));
}

/** The side panel's sections, each behind a dock button. */
type Panel = 'people' | 'files';

/** The place: the stage of everyone there, the dock, and the side panel with the people and files. */
function placeScreen(link: string, tb: Connection, name: string): void {
  current = tb;
  const me = tb.id;
  /** Everyone there who gave a name, by ID, with that name. */
  let people = new Map<string, string>();
  let leaving = false;
  const nameOf = (id: string) => people.get(id) || id;
  const isPerson = (id: string) => id === me || people.has(id);
  /** Everyone there, this person first. */
  const peopleIds = () => [me, ...[...people.keys()].filter(id => id !== me)];

  // Who is there: everyone who gives a name.
  const named = State.publish(tb, 'name', name);
  const names = State.subscribe<string>(tb, 'name', () => updatePeople());
  const updatePeople = () => {
    const now = new Map<string, string>([[me, name]]);
    for (const [id, given] of names) if (id !== me && typeof given === 'string') now.set(id, given);
    people = now;
    renderPeople();
    talk.refresh();
    files.refresh();
  };

  // The People section.
  const peopleTitle = () => t('people', { count: people.size });
  const [peopleHeading, peopleCap] = [tx(peopleTitle), tx(peopleTitle)];
  const peopleList = h('ul', { id: 'people' });
  const alone = h('p', { id: 'alone', hidden: true }, () => t('alone'), copyButton(() => location.href, 'copyLink'));
  const renderPeople = () => {
    const ids = [...people.keys()].sort((a, b) => nameOf(a).localeCompare(nameOf(b)) || a.localeCompare(b));
    peopleHeading.data = peopleCap.data = peopleTitle();
    // Two lines a person: the name (whole, wrapping when long), then the ID.
    show(peopleList, ids.map(id => h('li', { 'data-id': id },
      avatar(nameOf(id), initials(nameOf(id)), 'small'),
      h('span', { class: 'person-text' },
        h('span', { class: 'person-name' }, nameOf(id)),
        id === me && [' ', h('span', { class: 'muted' }, () => t('you'))],
        h('code', { class: 'id', id: id === me ? 'identity' : undefined }, id)))));
    alone.hidden = people.size !== 1;
  };

  const talk = talkSection(tb, nameOf, isPerson, peopleIds);
  const files = filesSection(tb, nameOf, (id, meta) => board.place(id, meta), (id, meta) => board.shareFile(id, meta), id => board.removeFile(id), () => board.refresh());
  const board = boardSection(tb, files, () => {
    // On a phone, where More is shown, the panel is a sheet over the stage: it closes so the new item is in sight.
    if (opened && more.offsetParent) openTab(undefined);
    if (stage.dataset.view !== 'board') openTab('board');
  });

  // Whether this person is connected and over which transport, reconnecting, or closed.
  const stateText = () => {
    const [status, transport, reason] = [tb.status, tb.transport, tb.reason?.name];
    if (status === 'connected') return t('connected', { transport: transport ?? '' });
    return reason ? t('reconnecting', { reason: reasonText(lang, reason) }) : t('connecting');
  };
  const state = h('span', { class: 'pill', id: 'state', role: 'status', title: stateText });
  const showState = () => {
    const [status, transport, reason] = [tb.status, tb.transport, tb.reason?.name];
    state.dataset.state = status;
    state.dataset.transport = transport ?? '';
    state.title = stateText();
    show(state, stateText);
    if (status !== 'closed' || current !== tb) return;
    for (const part of [talk, files, board]) part.close();
    names.close();
    named.close();
    current = undefined;
    if (unloading) return;
    stored(session, INSIDE, null);
    enterScreen(link, { reason: leaving ? undefined : (reason ?? 'network') });
  };
  tb.onStatus(showState);

  // The stage and the side panel: the two sections are always in the document, one shown at a time.
  const stage = h('section', { id: 'stage', 'data-view': 'tiles' }, talk.notice, talk.tiles, board.view, alone);
  const sections: Record<Panel, HTMLElement> = {
    people: h('section', { class: 'sheet', 'data-panel': 'people', hidden: true }, panelHead(peopleHeading), h('div', { class: 'panel-body' }, peopleList)),
    files: files.element,
  };
  const panel = h('aside', { id: 'panel', hidden: true, onclick: (event: MouseEvent) => {
    if ((event.target as Element).closest('.panel-close')) openTab(undefined);
  } }, sections.people, sections.files);
  const scrim = h('div', { id: 'scrim', hidden: true, onclick: () => setMore(false) });

  // The dock: the switches, the board, the panels' buttons (behind More on a phone) and Leave.
  let opened: Panel | undefined;
  let opener: HTMLElement | undefined;
  const tabButton = (tab: Panel, name: IconName, caption: Child) => h('button', {
    type: 'button', 'data-tab': tab, 'aria-pressed': 'false', 'aria-controls': 'panel', onclick: () => {
      setMore(false);
      openTab(tab);
    },
  }, icon(name), h('span', { class: 'cap' }, caption));
  const tabs: Record<Panel, HTMLButtonElement> = {
    people: tabButton('people', 'people', peopleCap),
    files: tabButton('files', 'paperclip', () => t('files')),
  };
  const boardButton = h('button', { type: 'button', 'data-tab': 'board', 'aria-pressed': 'false', 'aria-controls': 'board-view', 'aria-label': () => t('board'), title: () => t('board'), onclick: () => openTab('board') },
    icon('board'), h('span', { class: 'cap' }, () => t('board')));
  /** Opens `tab`'s panel (closing it when it is open), shows or hides the board view, or closes the panel with undefined. */
  const openTab = (tab: Panel | 'board' | undefined) => {
    if (tab === 'board') {
      const on = stage.dataset.view !== 'board';
      stage.dataset.view = on ? 'board' : 'tiles';
      board.view.hidden = !on;
      boardButton.setAttribute('aria-pressed', String(on));
      return;
    }
    const next = tab === opened ? undefined : tab;
    opened = next;
    panel.hidden = !next;
    if (next) panel.dataset.tab = next;
    else delete panel.dataset.tab;
    for (const [each, section] of Object.entries(sections)) section.hidden = each !== next;
    for (const [each, button] of Object.entries(tabs)) button.setAttribute('aria-pressed', String(each === next));
    if (next) {
      opener = tabs[next];
      sections[next].querySelector('h2')?.focus();
    } else {
      // Back to the button that opened it, or on a phone, where the tabs are behind More, to More.
      [opener, more, boardButton].find(button => button?.offsetParent)?.focus();
      opener = undefined;
    }
  };
  const more = iconButton({ id: 'more', 'aria-expanded': 'false', onclick: () => setMore(dock.dataset.more !== 'true') }, 'dots', 'more');
  const setMore = (on: boolean) => {
    dock.dataset.more = String(on);
    more.setAttribute('aria-expanded', String(on));
    scrim.hidden = !on;
  };
  const dock = h('footer', { id: 'dock', 'data-more': 'false' },
    h('div', { class: 'dock-me' }, avatar(name, initials(name), 'small'), h('span', { class: 'dock-name' }, name)),
    h('div', { class: 'dock-main' }, talk.switches,
      iconButton({ id: 'settings', popovertarget: 'settings-menu' }, 'sliders', 'settings'),
      boardButton, more,
      iconButton({ id: 'leave', class: 'danger', onclick: () => {
        leaving = true;
        tb.close();
      } }, 'leave', 'leave')),
    h('div', { class: 'dock-panels' }, tabs.people, tabs.files));

  app.className = 'place';
  app.onkeydown = (event: KeyboardEvent) => {
    // Escape closes the settings menu by itself (a popover); that press closes nothing else.
    if (event.key !== 'Escape' || talk.settingsMenu.matches(':popover-open')) return;
    if (dock.dataset.more === 'true') setMore(false);
    else if (opened) openTab(undefined);
    else if (stage.dataset.view === 'board') {
      openTab('board');
      boardButton.focus();
    }
  };
  show(app, topbar(state),
    h('main', { id: 'place' }, stage, panel, files.input, scrim),
    dock, talk.settingsMenu);
  showState();
  updatePeople();
}

const link = parseCollabLink(location.hash);
const name = stored(local, NAME);
if (!link) formScreen();
else if (name && stored(session, INSIDE) === location.hash) enter(link, name);
else enterScreen(link);
